import React, { useEffect, useMemo } from "react";
import {
  ContentLayout,
  Header,
  SpaceBetween,
  Box,
  FormField,
  Select,
  Grid,
} from "@cloudscape-design/components";
import { useNavigate } from "react-router-dom";
import ActivityListItem from "./components/ActivityListItem";
import { TrajectoryLoader } from "../common/Loaders";
import { useActivityFiltering } from "./hooks/useActivityFiltering";
import { useActivitySorting } from "./hooks/useActivitySorting";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useGetAllWorkflowExecutions } from "../../api/hooks";
import { transformToActivityItem } from "../../utils/activityTransformUtils";
import { ActivityItem } from "./types";
import "./all-activities.scss";

const AllActivities: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSpaceId, ensureSpaceSelected } = useGlobalStore();
  const { workflows, fetchWorkflows, isLoading: isWorkflowsLoading } = useWorkflowStore();
  const { data: executionsData, isLoading: isExecutionsLoading, error } = useGetAllWorkflowExecutions(selectedSpaceId || undefined);

  // Ensure space is selected and fetch workflows
  useEffect(() => {
    const initializeData = async () => {
      try {
        await ensureSpaceSelected();
      } catch (err) {
        console.error('Failed to initialize space:', err);
      }
    };

    initializeData();
  }, [ensureSpaceSelected]);

  // Fetch workflows when selectedSpaceId is available
  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflows]);

  // Transform API data to ActivityItem format
  const activitiesData = useMemo((): ActivityItem[] => {
    if (!executionsData?.results) {
      return [];
    }

    try {
      return executionsData.results.map(execution => {
        const workflow = workflows?.find(w => w.id === execution.workflowId);
        return transformToActivityItem(execution, workflow);
      });
    } catch (err) {
      console.error('Failed to transform workflow executions:', err);
      return [];
    }
  }, [executionsData, workflows]);

  const {
    filteredActivities,
    reportTypeFilterOptions,
    reportNameFilterOptions,
    statusFilterOptions,
    selectedReportType,
    selectedReportName,
    selectedStatus,
    setSelectedReportType,
    setSelectedReportName,
    setSelectedStatus,
    isLoading: isFilterLoading,
  } = useActivityFiltering({
    activities: activitiesData,
    isServerSide: false,
  });

  const {
    sortedActivities,
    sortOptions,
    selectedSort,
    setSelectedSort,
  } = useActivitySorting({
    activities: filteredActivities,
  });

  const handleView = (activity: ActivityItem) => {
    console.log("View activity:", activity.reportName);
    // TODO: Implement view functionality
  };

  const handleDownload = (activity: ActivityItem) => {
    console.log("Download activity:", activity.reportName);
    // TODO: Implement download functionality
  };

  const handleDelete = (activity: ActivityItem) => {
    console.log("Delete activity:", activity.reportName);
    // TODO: Implement delete functionality
  };

  // Show loading state while fetching executions or workflows
  const isLoading = isExecutionsLoading || isWorkflowsLoading || isFilterLoading;

  return (
    <ContentLayout
      defaultPadding
      header={
        <div className="activities-header">
          <div className="header-content">
            <div className="header-text">
              <h1 className="header-title">All Activities</h1>
              <p className="header-description">View and manage the outputs of your reports/analyses</p>
            </div>
            <div className="header-actions">
              <FormField label="Sort By">
                <Select
                  selectedOption={sortOptions.find(option => option.value === selectedSort) || null}
                  onChange={({ detail }) => setSelectedSort(detail.selectedOption.value || 'date-desc')}
                  options={sortOptions}
                  placeholder="Most Recent First"
                />
              </FormField>
            </div>
          </div>
        </div>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Filters Section */}
        <Box padding={{ vertical: "s" }}>
          <div className="activities-filters">
            <h3 className="filters-title">Filters</h3>
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
              <FormField label="Report/Analysis Type">
                <Select
                  selectedOption={reportTypeFilterOptions.find(option => option.value === selectedReportType) || null}
                  onChange={({ detail }) => {
                    setSelectedReportType(detail.selectedOption.value || 'all');
                    // Reset report name when type changes
                    setSelectedReportName('all');
                  }}
                  options={reportTypeFilterOptions}
                  placeholder="All Types"
                />
              </FormField>

              <FormField label="Report/Analysis">
                <Select
                  selectedOption={reportNameFilterOptions.find(option => option.value === selectedReportName) || null}
                  onChange={({ detail }) => setSelectedReportName(detail.selectedOption.value || 'all')}
                  options={reportNameFilterOptions}
                  placeholder="All Reports/Analyses"
                />
              </FormField>

              <FormField label="Status">
                <Select
                  selectedOption={statusFilterOptions.find(option => option.value === selectedStatus) || null}
                  onChange={({ detail }) => setSelectedStatus(detail.selectedOption.value || 'all')}
                  options={statusFilterOptions}
                  placeholder="All Statuses"
                />
              </FormField>
            </Grid>
          </div>
        </Box>

        {/* Activities List */}
        <Box>
          {isLoading ? (
            <div className="loading-state">
              <TrajectoryLoader size="140px" speed="2s" />
              <div className="loading-message">
                Loading activities...
              </div>
            </div>
          ) : error ? (
            <Box textAlign="center" padding="xxl">
              <Box variant="strong" textAlign="center" color="inherit">
                Error loading activities
              </Box>
              <Box variant="p" padding={{ bottom: "s" }} textAlign="center" color="inherit">
                Failed to load workflow executions. Please try refreshing the page.
              </Box>
            </Box>
          ) : sortedActivities.length === 0 ? (
            <Box textAlign="center" padding="xxl">
              <Box variant="strong" textAlign="center" color="inherit">
                No activities found
              </Box>
              <Box variant="p" padding={{ bottom: "s" }} textAlign="center" color="inherit">
                No activities match your current filter criteria. Try adjusting your filters or check back later.
              </Box>
            </Box>
          ) : (
            <div className="activities-content">
              <SpaceBetween direction="vertical" size="m">
                {sortedActivities.map((activity) => (
                  <ActivityListItem
                    key={activity.id}
                    activity={activity}
                    onView={handleView}
                    onDownload={handleDownload}
                    onDelete={handleDelete}
                  />
                ))}
              </SpaceBetween>
            </div>
          )}
        </Box>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default AllActivities;
